import React from 'react';
import kebabCase from 'lodash/kebabCase';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';

export interface IThemeHeroBannerProps extends IBaseElementProps {
	/**
	 * Banner Heading
	 */
	heading?: string;
	/**
	 * Banner Text
	 * @control textarea
	 */
	tagline?: string;
	link?: ILink;
	/**
	 * Banner Image |
	 * For Default Design - Image should be 986px X 739px |
	 * For Option 2 and 4 - Image should be 1440px X 683px |
	 * For Option 3 - Image should be 736px X 683px
	 */
	image?: IImage;
	/**
	 * 'fill' scales the image to fill the entire image area, leaving no empty space.
	 * 'fit' scales the image to fit within the height or width of the image area.
	 * 'original' uses the original image size if it's smaller than the image area.
	 */
	imageSize?: 'fill' | 'fit'| 'original';
	/**
	 * *note: Depending on the image's dimensions, certain options may not produce any affect on the image position.
	 */
	imagePosition?:
		| 'top left'
		| 'top center'
		| 'top right'
		| 'left'
		| 'center'
		| 'right'
		| 'bottom left'
		| 'bottom center'
		| 'bottom right';
	/**
	 * Banner Style Options Shown in Design
	 */
	bannerStyle?: 'Default' | 'Option 2' | 'Option 3' | 'Option 4';
}

/**
 * Theme Hero Banner
 * @block
 * @requiresHooks
 * @icon format-image
 */
const ThemeHeroBanner: React.FC<IThemeHeroBannerProps> = ({
	className,
	classNames = [],
	heading,
	tagline,
	link,
	image,
	imagePosition = 'center',
	imageSize = 'fill',
	bannerStyle = 'Default',
}) => {
	let content = null;
	switch (bannerStyle) {
		case 'Default':
			content = (
				<div className={cn(`banner-style-${kebabCase(bannerStyle)}`)}>
					<div className="container-fluid">
						<div className="row">
							<div className="col-12 col-lg-left">
								<div className="hero-banner__content">
									{heading && (
										<h1 className="hero-banner__heading">
											{heading}
										</h1>
									)}
									{tagline && (
										<p className="hero-banner__tagline">
											{tagline}
										</p>
									)}
									{link?.label &&
										link.label.length > 0 && ( // button only appears if link obj has label
										<Button
											className="btn btn-primary btn-lg"
											{...link}
										/>
									)}
								</div>
							</div>
							<div className="col-12 col-lg-right">
								<div className="hero-banner__image">
									{image && (
										<Image
											className={cn(
												'img-fluid',
												imagePosition,
												imageSize,
											)}
											{...image}
										/>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			);
			break;
		case 'Option 2':
			content = (
				<div className={cn(`banner-style-${kebabCase(bannerStyle)}`, !image?.src && 'no-image')}>
					<div className="container-fluid">
						<div className="row">
							<div className="hero-banner__background-overlay" />
							<div
								className={cn(
									'hero-banner__background-image',
									imagePosition,
									imageSize,
								)}
								style={
									image
										? {
											backgroundImage: `url("${image.src}")`,
										  }
										: {}
								}
							/>
							<div className="col-12 col-lg-6">
								<div className="hero-banner__content">
									{heading && (
										<h1 className="hero-banner__heading">
											{heading}
										</h1>
									)}
									{tagline && (
										<p className="hero-banner__tagline">
											{tagline}
										</p>
									)}
									{link?.label && link.label.length > 0 && (
										<Button
											className="btn btn-primary btn-lg"
											{...link}
										/>
									)}
								</div>
							</div>
							<div className="col-12 col-lg-6" />
						</div>
					</div>
				</div>
			);
			break;
		case 'Option 3':
			content = (
				<div className={cn(`banner-style-${kebabCase(bannerStyle)}`)}>
					<div className="container-fluid">
						<div className="row">
							<div className="col-12 col-lg-6 col-lg-left">
								<div className="hero-banner__content">
									{heading && (
										<h1 className="hero-banner__heading">
											{heading}
										</h1>
									)}
									{tagline && (
										<p className="hero-banner__tagline">
											{tagline}
										</p>
									)}
									{link?.label && link.label.length > 0 && (
										<Button
											className="btn btn-primary btn-lg"
											{...link}
										/>
									)}
								</div>
							</div>
							<div className="col-12 col-lg-6 col-lg-right">
								<div className="hero-banner__image">
									{image && (
										<Image
											className={cn(
												'img-fluid',
												imagePosition,
												imageSize,
											)}
											{...image}
										/>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			);
			break;
		case 'Option 4':
			content = (
				<div className={cn(`banner-style-${kebabCase(bannerStyle)}`, !image?.src && 'no-image')}>
					<div className="container-fluid">
						<div className="row">
							<div className="hero-banner__background-overlay" />
							<div
								className={cn(
									'hero-banner__background-image',
									imagePosition,
									imageSize,
								)}
								style={
									image
										? {
											backgroundImage: `url("${image.src}")`,
										  }
										: {}
								}
							/>
							<div className="col-12 col-lg-6">
								<div className="hero-banner__content">
									{heading && (
										<h1 className="hero-banner__heading">
											{heading}
										</h1>
									)}
									{tagline && (
										<p className="hero-banner__tagline">
											{tagline}
										</p>
									)}
									{link?.label && link.label.length > 0 && (
										<Button
											className="btn btn-primary btn-lg"
											{...link}
										/>
									)}
								</div>
							</div>
							<div className="col-12 col-lg-6" />
						</div>
					</div>
				</div>
			);
			break;
	}

	return (
		<div className={className || cn('theme-hero-banner', ...classNames)}>
			{content}
		</div>
	);
};

export default ThemeHeroBanner;
